import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, getActiveClubName, getActiveClubLogo } from "../lib/api";
import { ArrowLeft, Printer } from "lucide-react";

export default function PlayerProfile() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [players, setPlayers] = useState([]);

  useEffect(() => {
    (async () => {
      const [d, ps] = await Promise.all([api.playerStats(id), api.listPlayers()]);
      setData(d);
      setPlayers(ps);
    })();
  }, [id]);

  const opponentRecords = useMemo(() => players.filter((player) => player.id !== id).map((opponent) => {
    const directMatches = (data?.matches || []).filter((match) => match.player1_id === opponent.id || match.player2_id === opponent.id);
    let wins = 0; let draws = 0; let losses = 0; let myLegs = 0; let opponentLegs = 0;
    let myBulls = 0; let opponentBulls = 0;
    directMatches.forEach((match) => {
      const iAmPlayer1 = match.player1_id === id;
      myLegs += iAmPlayer1 ? match.legs_p1 : match.legs_p2;
      opponentLegs += iAmPlayer1 ? match.legs_p2 : match.legs_p1;
      if (match.winner_id === id) wins += 1;
      if (match.winner_id === opponent.id) losses += 1;
      if (match.is_historic_result && match.winner_id === null && match.legs_p1 === match.legs_p2) draws += 1;
      (match.legs || []).forEach((leg) => {
        if (!leg.ausgebullt) return;
        if (leg.winner_id === id) myBulls += 1;
        if (leg.winner_id === opponent.id) opponentBulls += 1;
      });
    });
    return { opponent, games: directMatches.length, wins, draws, losses, myLegs, opponentLegs, myBulls, opponentBulls };
  }).sort((a, b) => a.opponent.name.localeCompare(b.opponent.name, "de")), [data?.matches, id, players]);

  if (!data) return <div className="font-display text-2xl">Lade...</div>;
  const p = data.player;
  const clubName = getActiveClubName();
  const clubLogo = getActiveClubLogo();
  const winRate = data.matches_played > 0 ? Math.round((data.wins / data.matches_played) * 100) : 0;

  return (
    <>
      <style>{`
        @page { size: A4; margin: 7mm; }
        @media print {
          html, body, #root, .App, .App > *, .App > * > * { height: auto !important; min-height: 0 !important; }
          body { background: white !important; color: #111 !important; }
          body::before, header, footer, .no-print { display: none !important; }
          main { padding: 0 !important; margin: 0 !important; min-height: 0 !important; }
          .player-profile { max-width: 100% !important; color: #111 !important; font-size: 9px !important; line-height: 1.15 !important; }
          .player-profile * { color: #111 !important; border-color: #999 !important; }
          .player-profile .print-muted { color: #555 !important; }
          .player-profile .print-accent { color: #c62828 !important; }
          .player-profile > * + * { margin-top: 7px !important; }
          .player-profile .profile-hero { padding: 10px !important; }
          .player-profile .profile-hero h1 { font-size: 34px !important; line-height: 1 !important; margin-top: 2px !important; }
          .player-profile .profile-stats { gap: 5px !important; }
          .player-profile .profile-stats .dc-border { padding: 6px !important; }
          .player-profile .profile-stats .font-display { font-size: 24px !important; line-height: 1 !important; }
          .player-profile .profile-opponents-section { padding: 9px !important; }
          .player-profile .profile-opponents-section h2 { font-size: 21px !important; line-height: 1 !important; }
          .player-profile .profile-opponents-section p { margin: 3px 0 5px !important; }
          .player-profile .profile-opponents-section th { padding-top: 3px !important; padding-bottom: 3px !important; }
          .player-profile .profile-opponents-section td { padding-top: 3px !important; padding-bottom: 3px !important; }
          .player-profile .profile-print-footer { display: none !important; }
        }
      `}</style>
    <div className="space-y-6 player-profile" data-testid={`profile-${id}`}>
      <div className="flex flex-wrap justify-between gap-3 no-print">
      <Link to="/tabelle" className="inline-flex items-center gap-1 text-xs uppercase tracking-widest" style={{ color: "var(--dc-muted)" }} data-testid="profile-back">
        <ArrowLeft size={14} /> Zurück
      </Link>
      <button className="dc-btn" onClick={() => window.print()} data-testid="profile-print-button"><Printer size={14} /> Als PDF speichern / drucken</button>
      </div>
      <div className="dc-border p-8 relative overflow-hidden profile-hero" style={{ background: "linear-gradient(135deg, rgba(35,148,88,0.10), rgba(0,0,0,0))" }}>
        <div className="flex items-center gap-5">
          {clubLogo && <img src={clubLogo} alt={`${clubName} Logo`} className="w-20 h-20 object-contain dc-border p-1" style={{ background: "#fff" }} />}
          <div>
        <div className="text-xs uppercase tracking-[0.4em]" style={{ color: "var(--dc-chalk)" }}>{clubName}</div>
        <h1 className="font-display text-6xl mt-1">{p.name}</h1>
        {p.nickname && <div className="text-lg" style={{ color: "var(--dc-muted)" }}>&quot;{p.nickname}&quot;</div>}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 profile-stats">
        <Stat label="Partien" value={data.matches_played} />
        <Stat label="Siege" value={data.wins} color="var(--dc-secondary)" />
        {data.draws > 0 && <Stat label="Unentschieden" value={data.draws} color="var(--dc-chalk)" />}
        <Stat label="Niederlagen" value={data.losses} color="var(--dc-primary)" />
        <Stat label="Winrate" value={`${winRate}%`} color="var(--dc-chalk)" />
        <Stat label="Average" value={data.average} />
        <Stat label="Finish-Ø" value={data.finish_average || "—"} color="var(--dc-chalk)" />
        <Stat label="180er" value={data.s180} color="var(--dc-primary)" />
        <Stat label="High Finish" value={data.high_finish} color="var(--dc-chalk)" />
        <Stat label="High Finishes" value={data.high_finish_count || 0} color="var(--dc-chalk)" />
        <Stat label="Ausgebullt" value={data.ausgebullt} color="var(--dc-secondary)" />
        <Stat label="Zu-Null-Siege" value={data.clean_sweeps || 0} color="var(--dc-secondary)" />
        <Stat label="Entscheidungsleg-Bilanz" value={`${data.decider_wins || 0}:${data.decider_losses || 0}`} color="var(--dc-chalk)" />
        <Stat label="Form letzte 5" value={(data.recent_form || []).join(" - ") || "—"} color="var(--dc-primary)" />
        <Stat label="Beste Siegesserie" value={data.best_win_streak || "—"} color="var(--dc-chalk)" />
        <Stat label="Aktuelle Siegesserie" value={data.current_win_streak || "—"} color="var(--dc-secondary)" />
      </div>

      <div className="dc-border p-6 profile-opponents-section">
        <h2 className="font-display text-2xl mb-1">Direktvergleich gegen die Liga</h2>
        <p className="text-sm mb-4 print-muted" style={{ color: "var(--dc-muted)" }}>Bilanz gegen jeden möglichen Gegner der aktuellen Gruppe.</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="profile-opponents">
            <thead className="text-xs uppercase print-muted" style={{ color: "var(--dc-muted)" }}>
              <tr><th className="text-left py-2">Gegner</th><th className="text-center">Matches</th><th className="text-center">Bilanz</th><th className="text-center">Legs</th><th className="text-left">Ausgebullte Legs</th></tr>
            </thead>
            <tbody>
              {opponentRecords.map((record) => <tr key={record.opponent.id} className="border-t dc-border" data-testid={`profile-opponent-${record.opponent.id}`}>
                <td className="py-3 font-medium"><Link to={`/spieler/${record.opponent.id}`} className="hover:underline">{record.opponent.name}</Link></td>
                <td className="text-center font-mono-data">{record.games}</td>
                <td className="text-center font-mono-data"><span style={{ color: "var(--dc-secondary)" }}>{record.wins}</span>{record.draws > 0 && <> / {record.draws}</>} : <span style={{ color: "var(--dc-primary)" }}>{record.losses}</span></td>
                <td className="text-center font-mono-data">{record.myLegs} : {record.opponentLegs}</td>
                <td className="print-muted" style={{ color: "var(--dc-muted)" }}>{p.name}: {record.myBulls} · {record.opponent.name}: {record.opponentBulls}</td>
              </tr>)}
              {opponentRecords.length === 0 && <tr><td colSpan={5} className="py-6 text-center print-muted" style={{ color: "var(--dc-muted)" }}>Noch keine weiteren Spieler in dieser Gruppe.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      <p className="text-center text-xs print-muted profile-print-footer" style={{ color: "var(--dc-muted)" }}>Erstellt am {new Date().toLocaleDateString("de-DE")} · {getActiveClubName()}</p>
    </div>
    </>
  );
}

function Stat({ label, value, color = "var(--dc-text)" }) {
  return (
    <div className="dc-border p-4" data-testid={`stat-${label.toLowerCase()}`}>
      <div className="text-[10px] uppercase tracking-widest" style={{ color: "var(--dc-muted)" }}>{label}</div>
      <div className="font-display text-4xl font-mono-data mt-1" style={{ color }}>{value}</div>
    </div>
  );
}
